/**
 * Big-payload cache for international flight result lists.
 *
 * International roundtrip results run to several MB once fare classes are
 * included, which is well past what session/localStorage can hold, so the
 * primary store is IndexedDB (structured clone, no JSON parse on the main
 * thread). An in-memory Map sits in front of it so returning to the results
 * page inside the same tab is instant, and sessionStorage is only used as a
 * fallback when IndexedDB is unavailable (private mode, blocked storage).
 *
 * Entries expire after the international window in config/constants: reads drop
 * anything older, and expired rows are purged in the background. Rows written
 * by a different release are dropped on read as well - see
 * CACHE_SCHEMA_VERSION.
 */

import { getFlightSearchCacheDuration } from "../config/constants";

const DB_NAME = "flymyaInterFlightResultCache";
const DB_VERSION = 1;
const STORE_NAME = "interFlightResults";
const SESSION_KEY_PREFIX = "interFlightResultCache:";
// A search's selection record sits beside its lists under this suffix.
const SELECTION_SUFFIX = "::selection";

/**
 * A selection belongs to one tab, the result lists do not.
 *
 * The store is IndexedDB, which every tab on the origin shares, and the search
 * key is built from search parameters alone - so two tabs running the SAME
 * search were reading and writing one selection record. Picking a flight in one
 * tab overwrote the other's, and either tab re-fetching wiped both. Sharing the
 * lists is the whole point of the cache and stays; the selection is per tab.
 *
 * sessionStorage is the right home for the id: it is per tab and survives a
 * reload, which is exactly the lifetime a restored selection should have. If it
 * is unavailable the id lives for this page load only, which degrades to "no
 * selection restored after reload" rather than to cross-tab bleed.
 */
const TAB_ID_KEY = "interFlightTabId";
let fallbackTabId = null;

const getTabId = () => {
  const mint = () =>
    `t${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
  try {
    if (typeof window === "undefined" || !window.sessionStorage) {
      fallbackTabId = fallbackTabId || mint();
      return fallbackTabId;
    }
    let id = window.sessionStorage.getItem(TAB_ID_KEY);
    if (!id) {
      id = mint();
      window.sessionStorage.setItem(TAB_ID_KEY, id);
    }
    return id;
  } catch (err) {
    fallbackTabId = fallbackTabId || mint();
    return fallbackTabId;
  }
};

// The international window from the shared config, so tuning it there moves
// this cache with it rather than leaving two different ten minutes.
export const INTER_FLIGHT_RESULT_CACHE_TTL = getFlightSearchCacheDuration("i");

/**
 * How many searches' lists are kept at once. Each international payload runs to
 * several MB, and entries only leave on expiry, so a burst of searches inside
 * one TTL window could hold tens of MB until the sweep. The oldest are evicted
 * past this count; a cache miss is always an acceptable outcome.
 */
const MAX_CACHED_SEARCHES = 8;

/**
 * Shape of a stored entry. Bump when the payload written by
 * writeInterFlightResultCache changes shape - the fields
 * hydrateFromCachedResults reads, say. IndexedDB outlives the tab and the
 * browser session, so without this a release could hydrate a payload written by
 * the previous one; a mismatch reads as a plain miss, not as an expiry, because
 * nothing about the traveller's prices has gone stale.
 */
const CACHE_SCHEMA_VERSION = 1;

// L1: survives in-app navigation (back from flight details) without any
// deserialisation cost. Lost on hard reload, where IndexedDB takes over.
const memoryCache = new Map();

const isCurrentVersion = (entry) =>
  Boolean(entry) && entry.version === CACHE_SCHEMA_VERSION;

const isExpired = (entry) =>
  !entry ||
  typeof entry.savedAt !== "number" ||
  Date.now() - entry.savedAt > INTER_FLIGHT_RESULT_CACHE_TTL;

/**
 * Searches whose cached results were found past their TTL. Reading an expired
 * entry deletes it, so without this the *first* read would be the only one that
 * could ever report the expiry — and the results page reads more than once per
 * visit (the airport list arriving re-runs the search). The mark is deliberately
 * not cleared by a later write: the re-fetch that follows an expiry would
 * otherwise erase the very signal the page is waiting to show. It is dropped
 * only when the page acknowledges it, via clearInterFlightSearchExpired.
 */
const expiredSearchKeys = new Set();

export const hasInterFlightSearchExpired = (key) => expiredSearchKeys.has(key);

export const clearInterFlightSearchExpired = (key) => {
  expiredSearchKeys.delete(key);
};

/**
 * Builds a stable key from everything that can change the returned fares.
 * Anything missing here would risk showing one search's results for another.
 */
export const buildInterFlightResultCacheKey = (parts = {}) => {
  const {
    fromIata,
    toIata,
    startDate,
    endDate,
    adult,
    child,
    infant,
    nationality,
    flightType,
    tripType,
    departureUUID,
    classTypeId,
    isB2B,
    isCallCenter,
    isLoggedIn,
    SF,
  } = parts;

  return [
    "interRT",
    flightType ?? "",
    tripType ?? "",
    (fromIata ?? "").toLowerCase(),
    (toIata ?? "").toLowerCase(),
    startDate ?? "",
    endDate ?? "",
    `${adult ?? 0}-${child ?? 0}-${infant ?? 0}`,
    nationality ?? "",
    departureUUID ?? "0",
    classTypeId ?? "",
    isB2B ? "b2b" : "b2c",
    isCallCenter ? "cc" : "nocc",
    isLoggedIn ? "auth" : "guest",
    SF ?? "",
  ].join("|");
};

let dbPromise = null;

const openDb = () => {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      resolve(null);
      return;
    }

    let request;
    try {
      request = window.indexedDB.open(DB_NAME, DB_VERSION);
    } catch (error) {
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

  return dbPromise;
};

const withStore = async (mode, run) => {
  const db = await openDb();
  if (!db) return null;

  return new Promise((resolve) => {
    let transaction;
    try {
      transaction = db.transaction(STORE_NAME, mode);
    } catch (error) {
      resolve(null);
      return;
    }

    const store = transaction.objectStore(STORE_NAME);
    let result = null;

    try {
      run(store, (value) => {
        result = value;
      });
    } catch (error) {
      resolve(null);
      return;
    }

    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => resolve(null);
    transaction.onabort = () => resolve(null);
  });
};

// sessionStorage fallback — only reached when IndexedDB is unavailable, and
// only useful for smaller payloads (it throws once the ~5MB quota is hit).
const readFromSession = (key) => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY_PREFIX + key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const writeToSession = (key, entry) => {
  try {
    window.sessionStorage.setItem(
      SESSION_KEY_PREFIX + key,
      JSON.stringify(entry),
    );
  } catch (error) {
    // Quota exceeded or storage disabled: the memory cache still covers
    // back-navigation, we just lose the reload case.
  }
};

const removeFromSession = (key) => {
  try {
    window.sessionStorage.removeItem(SESSION_KEY_PREFIX + key);
  } catch (error) {
    // ignore
  }
};

export const removeInterFlightResultCache = async (key) => {
  if (!key) return;

  memoryCache.delete(key);
  removeFromSession(key);
  await withStore("readwrite", (store) => {
    store.delete(key);
  });
};

/**
 * Reads the cached payload for this search.
 *
 * Returns `{ data, expired, savedAt }`. `expired: true` means results for this
 * exact search *were* cached but are older than the TTL (the entry is removed on
 * the way out) — callers use it to tell "never searched this" apart from "the
 * prices they were looking at have gone stale". `data` is null in both those
 * cases. `savedAt` is the write time of a live entry, so a caller can tell how
 * much of the TTL is left for the results it is about to render.
 */
export const readInterFlightResultCache = async (key) => {
  if (!key || typeof window === "undefined") {
    return { data: null, expired: false };
  }

  const expire = async () => {
    expiredSearchKeys.add(key);
    await removeInterFlightResultCache(key);
    return { data: null, expired: true, savedAt: null };
  };

  const discard = async () => {
    await removeInterFlightResultCache(key);
    return { data: null, expired: false, savedAt: null };
  };

  const fromMemory = memoryCache.get(key);
  if (fromMemory) {
    if (!isCurrentVersion(fromMemory)) return discard();
    if (!isExpired(fromMemory)) {
      return {
        data: fromMemory.data,
        expired: false,
        savedAt: fromMemory.savedAt,
      };
    }
    return expire();
  }

  let entry = await withStore("readonly", (store, setResult) => {
    const request = store.get(key);
    request.onsuccess = () => setResult(request.result || null);
  });

  if (!entry) entry = readFromSession(key);
  // Nothing stored: still report an expiry this page already detected, so a
  // second read cannot swallow it.
  if (!entry) return { data: null, expired: expiredSearchKeys.has(key) };

  // Written by another release: unreadable rather than stale.
  if (!isCurrentVersion(entry)) return discard();

  if (isExpired(entry)) return expire();

  memoryCache.set(key, entry);
  return { data: entry.data, expired: false, savedAt: entry.savedAt };
};

const isSelectionKey = (key) => String(key).includes(SELECTION_SUFFIX);

/**
 * Drops the oldest searches once more than MAX_CACHED_SEARCHES are held, so a
 * run of searches inside one TTL window cannot grow the store without bound. A
 * search's selection record goes with its lists - on its own it would restore a
 * chosen flight onto a list that is no longer there. The key just written is
 * never a candidate.
 */
const evictOverflow = async (keepKey) => {
  const entries = await withStore("readonly", (store, setResult) => {
    const rows = [];
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        setResult(rows);
        return;
      }
      const { key, savedAt } = cursor.value || {};
      if (key && !isSelectionKey(key))
        rows.push({ key, savedAt: savedAt || 0 });
      cursor.continue();
    };
  });

  if (!entries || entries.length <= MAX_CACHED_SEARCHES) return;

  const doomed = entries
    .filter((entry) => entry.key !== keepKey)
    .sort((a, b) => a.savedAt - b.savedAt)
    .slice(0, entries.length - MAX_CACHED_SEARCHES)
    .map((entry) => entry.key);

  await Promise.all(
    doomed.flatMap((key) => [
      removeInterFlightResultCache(key),
      removeInterFlightResultCache(selectionKey(key)),
    ]),
  );
};

/**
 * Stores the result lists for this search and stamps them with the write time.
 */
export const writeInterFlightResultCache = async (key, data) => {
  if (!key || !data || typeof window === "undefined") return;

  const entry = {
    key,
    version: CACHE_SCHEMA_VERSION,
    savedAt: Date.now(),
    data,
  };
  memoryCache.set(key, entry);

  const db = await openDb();
  if (!db) {
    writeToSession(key, entry);
    return;
  }

  await withStore("readwrite", (store) => {
    store.put(entry);
  });

  // The lists, not the selection that hangs off them, decide how much room a
  // search takes.
  if (!isSelectionKey(key)) {
    await evictOverflow(key).catch(() => {});
  }
};

/**
 * The flights the user picked are kept in their own record next to the lists,
 * under the same search key. Separate because selections change on every click
 * while the lists are written once, and re-writing megabytes per click would be
 * wasteful. Same store, so the same TTL and the same purge apply.
 */
const selectionKey = (searchKey) =>
  `${searchKey}${SELECTION_SUFFIX}${getTabId()}`;

/**
 * Bump whenever the shape of a stored selection changes - a field added to or
 * removed from INTER_RT_SELECTION_FIELDS, or a different shape inside one of
 * them. A blob from any other version is ignored rather than restored: the
 * restore applies whatever fields it finds, so a blob written by a different
 * build leaves the page half-restored, which is how a selected flight reached
 * the flight cards without its fare list and took the page down.
 */
const SELECTION_SCHEMA_VERSION = 1;

export const readInterFlightSelectionCache = (searchKey) =>
  readInterFlightResultCache(selectionKey(searchKey)).then((result) => {
    const stored = result.data;
    if (!stored || stored.version !== SELECTION_SCHEMA_VERSION) return null;
    return stored.selection || null;
  });

export const writeInterFlightSelectionCache = (searchKey, selection) =>
  writeInterFlightResultCache(selectionKey(searchKey), {
    version: SELECTION_SCHEMA_VERSION,
    selection,
  });

export const removeInterFlightSelectionCache = (searchKey) =>
  removeInterFlightResultCache(selectionKey(searchKey));

/**
 * Drops every entry past its TTL. Safe to call on page load; failures are
 * swallowed because a cache miss is always an acceptable outcome.
 */
export const purgeExpiredInterFlightResultCache = async () => {
  if (typeof window === "undefined") return;

  const isStale = (entry) => !isCurrentVersion(entry) || isExpired(entry);

  memoryCache.forEach((entry, key) => {
    if (isStale(entry)) memoryCache.delete(key);
  });

  try {
    Object.keys(window.sessionStorage).forEach((storageKey) => {
      if (!storageKey.startsWith(SESSION_KEY_PREFIX)) return;
      const entry = readFromSession(
        storageKey.slice(SESSION_KEY_PREFIX.length),
      );
      if (isStale(entry)) window.sessionStorage.removeItem(storageKey);
    });
  } catch (error) {
    // ignore
  }

  await withStore("readwrite", (store) => {
    const request = store.openCursor();
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) return;
      if (isStale(cursor.value)) cursor.delete();
      cursor.continue();
    };
  });
};

/**
 * Drops every cached search - lists, selections and expiry marks alike -
 * regardless of age. Used when the traveller starts a search from the home
 * page: that is a request for current prices, so nothing kept from an earlier
 * search in this tab may be restored behind it.
 *
 * Failures are swallowed: an empty cache is only ever an optimisation.
 */
export const clearInterFlightResultCache = async () => {
  memoryCache.clear();
  expiredSearchKeys.clear();

  if (typeof window === "undefined") return;

  try {
    Object.keys(window.sessionStorage).forEach((storageKey) => {
      if (storageKey.startsWith(SESSION_KEY_PREFIX))
        window.sessionStorage.removeItem(storageKey);
    });
  } catch (error) {
    // ignore
  }

  await withStore("readwrite", (store) => {
    store.clear();
  });
};
